import { ServiceProvider } from '../exports/Interfaces';

export interface MixpanelProject {
  token: string;
  /**
   * True when the token is the provider's own project, false on the FoodAmigos platform
   * fallback. The platform project is shared by every white-label partner that has no key of
   * its own, so anything that must stay inside the provider's own project (session replay) is
   * gated on this.
   */
  isOwnProject: boolean;
}

const nonBlank = (value?: null | string): null | string => (value?.trim() ? value : null);

/**
 * Which Mixpanel project the consumer's events go to: the provider's own key when it has one
 * (the behaviour before the WLP work, unchanged), otherwise FoodAmigos' platform project, so a
 * partner restaurant without its own key is tracked instead of not at all. Null when neither
 * token is present: Mixpanel is then never initialised.
 */
export function resolveMixpanelProject(
  serviceProvider?: null | Pick<Partial<ServiceProvider>, 'mixpanel_api_key' | 'platform_mixpanel_token'>,
): null | MixpanelProject {
  const ownKey = nonBlank(serviceProvider?.mixpanel_api_key);

  if (ownKey) {
    return { token: ownKey, isOwnProject: true };
  }

  const platformToken = nonBlank(serviceProvider?.platform_mixpanel_token);

  if (platformToken) {
    return { token: platformToken, isOwnProject: false };
  }

  return null;
}

/**
 * Init options only the platform fallback gets: UTM values are not persisted. By default the SDK
 * (`store_google` without `stop_utm_persistence`) registers the landing URL's utm_* as super properties,
 * keeps them in its mp_ cookie and sends them with every later event, and some mail senders put the
 * subscriber's email in utm_content / utm_term. With this the UTMs go only on the events of the page whose
 * URL carries them (`track_marketing`), and the SDK drops any it persisted before. A provider's own project
 * keeps the SDK default, as before the fallback existed.
 */
export const PLATFORM_PROJECT_UTM_CONFIG = {
  stop_utm_persistence: true,
};
